import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Service to handle generic HTTP requests.
 */
@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private _http = inject(HttpClient);

  /**
   * Performs an HTTP request.
   * @param {'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'} method HTTP method.
   * @param {string} url Request URL.
   * @param {any} [body] Request body.
   * @param {object} [params] Query parameters.
   * @param {boolean} [isSecured] Whether to include auth headers (handled by interceptor usually, but kept for compat).
   * @param {object} [customHeaders] Custom headers.
   * @returns {Observable<T>} Response observable.
   */
  request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    body?: any,
    params?: { [key: string]: string | number | boolean | string[] | number[] },
    isSecured: boolean = true,
    customHeaders?: { [key: string]: string }
  ): Observable<T> {
    const isFormData = body instanceof FormData;

    let headers = isFormData
      ? new HttpHeaders()
      : new HttpHeaders({ 'Content-Type': 'application/json' });

    if (customHeaders) {
      Object.keys(customHeaders).forEach(key => {
        headers = headers.set(key, customHeaders[key]);
      });
    }

    const options: { headers: HttpHeaders; params?: HttpParams } = {
      headers
    };

    if (params) {
      let httpParams = new HttpParams();
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null) {

          if (Array.isArray(value)) {
            value.forEach(item => {
              httpParams = httpParams.append(key, item.toString());
            });
          } else {
            httpParams = httpParams.set(key, value.toString());
          }
        }
      });
      options.params = httpParams;
    }

    switch (method) {
      case 'GET':
        return this._http.get<T>(url, options).pipe(map(response => response as T));
      case 'POST':
        return this._http.post<T>(url, body, options).pipe(map(response => response as T));
      case 'PUT':
        return this._http.put<T>(url, body, options).pipe(map(response => response as T));
      case 'PATCH':
        return this._http.patch<T>(url, body, options).pipe(map(response => response as T));
      case 'DELETE':
        if (body !== undefined) {
          return this._http.delete<T>(url, { ...options, body }).pipe(map(response => response as T));
        }
        return this._http.delete<T>(url, options).pipe(map(response => response as T));
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }
}
